import { useState, useEffect, useRef } from "react";
import { FaPen } from "react-icons/fa";
import { Editor } from 'primereact/editor';
import API from "../../api";
import { showError, showSuccess } from '../../utils/dialog';
import ActionDropdown from "../ActionDropdown";

const EditMessageForm = ({ message, onClose }) => {
  const [selectedTab, setSelectedTab] = useState("GENERAL");
  const [, setSelectedItems] = useState([]);
  // form fields
  const [name, setName] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [toField, setToField] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [customizeMessage, setCustomizeMessage] = useState(true);
  const [messageFormat, setMessageFormat] = useState("R");
  const [isSaving, setIsSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [testEmailTo, setTestEmailTo] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const attachmentInputRef = useRef(null);
  // ref to hold preview window between renders
  const previewWinRef = useRef(null);

  const normalizeFormat = (raw) => {
    const value = String(raw || "").trim().toUpperCase();
    if (value === "T" || value === "TEXT") return "T";
    if (value === "H" || value === "HTML") return "H";
    if (value === "R" || value === "RICH" || value === "RICH_TEXT" || value === "RICHTEXT") return "R";
    return "R";
  };

  // Fetch details when message prop changes
  useEffect(() => {
    if (!message || !message.message_id) return;

    const fetchDetails = async () => {
      try {
        const resp = await API.get(`/api/admin/messages/${message.message_id}`);
        const data = resp.data.data || {};
        const msg = data.message || {};
        const email = data.email || {};

        // Prefill from messages table
        setName(msg.name || message.name || "");
  // Prefer the explicit `tag` column from messages; avoid falling back to numeric id
  setReference(msg.tag || message.tag || msg.reference || msg.ref || msg.name || "");
        setDescription(msg.description || msg.discription || msg.message || "");

        // Prefill email fields if available (also accept top-level subject/body returned by API)
        setSubject(email.subject || data.subject || email.title || "");
        setEmailContent(email.body || data.body || email.content || email.email_body || "");
        setTemplateId(email.message_template_id || "");
        setCustomizeMessage(Number(email.customize ?? 1) === 1);
        setMessageFormat(normalizeFormat(email.format));

        // safe optional fields
        setFromEmail(email.from_email || msg.from_email || "");
        setFromName(email.from_name || msg.from_name || "");
        setReplyTo(email.reply_to || msg.reply_to || "");
        setToField(email.to || msg.to || "");
        setCc(email.cc || msg.cc || "");
        setBcc(email.bcc || msg.bcc || "");
      } catch (err) {
        console.error('Failed to fetch message details', err);
        // fallback to minimal prefill from passed message prop
        setName(message.name || "");
        setReference(message.tag || message.reference || message.message_id || "");
      }
    };

    fetchDetails();
    fetchAttachments();
  }, [message]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const resp = await API.get('/api/admin/messages/templates', { params: { limit: 500 } });
      setTemplates(resp.data?.data || []);
    } catch (err) {
      console.error('Failed to load templates', err);
      setTemplates([]);
    }
  };

  const fetchAttachments = async () => {
    if (!message || !message.message_id) return;
    try {
      const resp = await API.get(`/api/admin/messages/${message.message_id}/attachments`);
      setAttachments(resp.data?.data || []);
    } catch (err) {
      console.error('Failed to load attachments', err);
      setAttachments([]);
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!file || !message?.message_id) return;
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await API.post(
        `/api/admin/messages/${message.message_id}/attachments`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      await fetchAttachments();
      showSuccess("Attachment uploaded");
    } catch (err) {
      console.error("Attachment upload failed", err?.response?.data || err);
      showError("Failed to upload attachment");
    } finally {
      setIsUploading(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!message?.message_id) return;
    try {
      await API.delete(`/api/admin/messages/${message.message_id}/attachments/${attachmentId}`);
      await fetchAttachments();
      showSuccess("Attachment deleted");
    } catch (err) {
      console.error("Attachment delete failed", err);
      showError("Failed to delete attachment");
    }
  };

  const handleDownloadAttachment = (attachment) => {
    if (!message?.message_id || !attachment?.message_attachment_id) {
      showError("Attachment is not available");
      return;
    }

    (async () => {
      try {
        const resp = await API.get(
          `/api/admin/messages/${message.message_id}/attachments/${attachment.message_attachment_id}/download`,
          { responseType: "blob" },
        );
        const blobUrl = window.URL.createObjectURL(resp.data);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = attachment.filename || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error("Attachment download failed", err);
        showError("Failed to download attachment");
      }
    })();
  };

  // Save handler: persist message.name and email template fields to backend
  // This calls PUT /api/admin/messages/:id with a minimal set of fields.
  const saveMessage = async () => {
    if (!message || !message.message_id) {
      showError('No message selected');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        name,
        // reference/tag is displayed but not editable in this form; if you add an input, include tag here
        description,
        subject,
        body: emailContent,
        from_email: fromEmail,
        from_name: fromName,
        reply_to: replyTo,
        to: toField,
        cc,
        bcc,
        message_template_id: templateId || null,
        customize: customizeMessage ? 1 : 0,
        format: messageFormat,
      };

      const resp = await API.put(`/api/admin/messages/${message.message_id}`, payload);
      if (resp.data && resp.data.success) {
        // simple feedback — in future wire to toasts
        showSuccess('Saved message template');
        onClose && onClose();
      } else {
        console.error('Save response:', resp);
        showError('Failed to save message template');
      }
    } catch (err) {
      console.error('Save error', err);
      showError('Error saving message template');
    } finally {
      setIsSaving(false);
    }
  };

  // Preview handler: call preview API and open result in a new window for quick review
  const previewMessage = async () => {
    try {
      const resp = await API.post('/api/admin/messages/preview', { subject, body: emailContent, data: {}, message_template_id: templateId || null });
      if (resp.data && resp.data.success) {
        const rendered = resp.data.data || {};
        // Open a small popup window (or reuse existing) and write rendered HTML body for quick preview
        // Use a ref to keep track of the preview window so repeated previews reuse the same popup
        if (!previewWinRef.current || previewWinRef.current.closed) {
          // features: smaller popup window without toolbars, centered on screen
          const width = 600;
          const height = 500;
          const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
          const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
          const features = `menubar=0,toolbar=0,location=0,status=0,resizable=1,scrollbars=1,width=${width},height=${height},left=${left},top=${top}`;
          previewWinRef.current = window.open('', 'messagePreview', features);
        }

        const w = previewWinRef.current;
        if (w) {
            try {
            w.document.open();
            // Include subject and reference at top, then rendered HTML body
            const subj = (rendered.subject || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const refEscaped = String(reference || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const headerHtml = `<div style="font-family: Inter, Arial, Helvetica, sans-serif; padding:12px 16px; border-bottom:1px solid #eee"><div style="font-size:13px;color:#666;">Reference: ${refEscaped}</div><h3 style="margin:6px 0 0 0;font-size:16px;color:#222;">${subj}</h3></div>`;
            const bodyHtml = rendered.body || '<pre>' + (rendered.subject || '') + '</pre>';
            w.document.write(`<!doctype html><html><head><title>Preview - ${rendered.subject || ''}</title><meta charset="utf-8" /><meta name="viewport" content="width=device-width,initial-scale=1" /></head><body style="margin:0;">${headerHtml}<div style="padding:12px;">${bodyHtml}</div></body></html>`);
            w.document.close();
            w.focus();
          } catch (writeErr) {
            console.error('Failed to write preview window', writeErr);
            showError('Preview failed — your browser may block writing to popup.');
          }
        } else {
          showError('Popup blocked — allow popups or use browser preview');
        }
      } else {
        console.error('Preview API error', resp);
        showError('Failed to render preview');
      }
    } catch (err) {
      console.error('Preview error', err);
      showError('Error rendering preview');
    }
  };

  const sendTestEmail = async () => {
    if (!message || !message.message_id) return;
    const recipient = (testEmailTo || toField || "").trim();
    if (!recipient) {
      showError("Test email recipient is required");
      return;
    }
    try {
      const resp = await API.post(`/api/admin/messages/${message.message_id}/test-email`, {
        to: recipient,
        subject,
        body: emailContent,
        from_email: fromEmail,
        from_name: fromName,
        reply_to: replyTo,
        cc,
        bcc,
        message_template_id: templateId || null,
      });
      if (resp.data?.success) {
        showSuccess("Test email sent");
      } else {
        showError(resp.data?.message || "Failed to send test email");
      }
    } catch (err) {
      console.error("Test email error", err);
      showError("Failed to send test email");
    }
  };

  

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-[95%] sm:w-full max-w-4xl p-6 relative flex flex-col max-h-[90vh] overflow-y-auto overflow-x-hidden">
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
          >
          ✖
        </button>
  
        {/* Header */}
        <div className="flex">
          <FaPen className="text-buttonRed text-lg w-5 h-5 mt-1 mr-2"/>
          <h2 className="text-xl text-formTitleText font-semibold mb-2">Edit Message</h2>
        </div>

        <div className="h-[1px] bg-[#DC1F004D]"></div>

        <div className="flex space-x-8 mb-2 mt-4 border-b border-border">
          {["GENERAL", "EMAIL MESSAGES", "ATTACHMENTS"].map(tab => (
              <button
              key={tab}
              onClick={() => {
                  setSelectedTab(tab);
                  setSelectedItems([]); // Clear selections when changing tabs
              }}
              className={`pb-2 px-1 text-sm text-[#DC1F00] font-medium border-b-2 transition-colors ${
                  selectedTab === tab
                  ? "border-[#DC1F00] text-sidebar-active"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              >
              {tab}
              </button>
          ))}
        </div>

        {selectedTab === "GENERAL" && (
          <div className="flex-1 overflow-y-auto p-2">
            <div className="flex items-start justify-between gap-4">
              <div className="w-full">
                <label className="block text-sm text-textGray mb-2">Name <span className="text-[#00000080]">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* Right side - Reference display */}
              <div className="w-1/5">
                <label className="block text-sm text-textGray mb-2">Reference</label>
                <span className="text-sm text-gray-700">{reference || '—'}</span>
              </div>
            </div>

            <label className="block text-sm text-textGray mb-2">Discription</label>
            <input
              type="text"
              name="discription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
              <div>
                <label className="block text-sm text-textGray mb-2">From Email</label>
                <input
                  type="email"
                  name="Email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
                />
              </div>

              <div>
                <label className="block text-sm text-textGray mb-2">From Name</label>
                <input
                  type="text"
                  name="fromName"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
                />
              </div>

              <div>
                <label className="block text-sm text-textGray mb-2">Reply To</label>
                <input
                  type="text"
                  name="replyTo"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
                />
              </div>

              <div>
                <label className="block text-sm text-textGray mb-2">To</label>
                <input
                  type="text"
                  name="to"
                  value={toField}
                  onChange={(e) => setToField(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
                />
              </div>

              <div>
                <label className="block text-sm text-textGray mb-2">CC</label>
                <input
                  type="text"
                  name="cc"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
                />
              </div>

              <div>
                <label className="block text-sm text-textGray mb-2">BCC</label>
                <input
                  type="text"
                  name="bcc"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
                />
              </div>
            </div>
          </div>
        )}

        {selectedTab === "EMAIL MESSAGES" && (
          <div className="flex-1 overflow-y-auto p-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
              <div>
                <label className="block text-sm text-textGray mb-2">Customize Message <span className="text-[#00000080]">*</span></label>
                <select
                  value={customizeMessage ? "1" : "0"}
                  onChange={(e) => setCustomizeMessage(e.target.value === "1")}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400"
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-textGray mb-2">Format <span className="text-[#00000080]">*</span></label>
                <select
                  value={messageFormat}
                  onChange={(e) => setMessageFormat(normalizeFormat(e.target.value))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400"
                >
                  <option value="T">Text</option>
                  <option value="H">HTML</option>
                  <option value="R">Rich Text</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-textGray mb-2">Template</label>
                <select
                  value={templateId || ""}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-400"
                >
                  <option value="">None</option>
                  {templates.map((t) => (
                    <option key={t.message_template_id || t.id} value={t.message_template_id || t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="block text-sm text-textGray mb-2">Subject <span className="text-[#00000080]">*</span></label>
            <input
              type="text"
              name="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={!customizeMessage}
              className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400 disabled:bg-gray-100 disabled:text-gray-600"
            />

            <label className="block text-sm text-textGray mb-2">Content</label>
            {!customizeMessage ? (
              messageFormat === "T" ? (
                <pre className="w-full min-h-[250px] border rounded-lg px-3 py-2 mb-1 bg-gray-100 text-gray-700 whitespace-pre-wrap font-sans">
                  {emailContent || ""}
                </pre>
              ) : (
                <div
                  className="w-full min-h-[250px] border rounded-lg px-3 py-2 mb-1 bg-gray-100 text-gray-700"
                  dangerouslySetInnerHTML={{ __html: emailContent || "" }}
                />
              )
            ) : messageFormat === "T" ? (
              <textarea
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                className="w-full min-h-[250px] border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
              />
            ) : (
              <Editor
                value={emailContent}
                onTextChange={(e) => setEmailContent(e.htmlValue)}
                style={{ height: '250px', marginBottom: '5px'}}
              />
            )}

            <label className="block text-sm text-textGray mt-2 mb-2">Test Email To</label>
            <input
              type="email"
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              placeholder="name@example.com"
              className="w-full border rounded-lg px-3 py-2 mb-1 focus:ring-1 focus:ring-red-400"
            />
          </div>
        )}

        {selectedTab === "ATTACHMENTS" && (
          <div className="relative p-2 min-h-[200px] overflow-y-auto">
            {/* Upload Button */}
            <button
              className="absolute top-2 right-2 bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full"
              onClick={() => attachmentInputRef.current && attachmentInputRef.current.click()}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </button>
            <input
              ref={attachmentInputRef}
              type="file"
              className="hidden"
              onChange={(e) => handleUploadAttachment(e.target.files?.[0])}
            />

            {/* Attachments content area */}
            <div className="mt-14 pr-20 text-sm text-gray-600 flex flex-col gap-3">
              {attachments.length === 0 ? (
                <p>No attachments uploaded yet.</p>
              ) : (
                attachments.map((a) => (
                  <div key={a.message_attachment_id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                    <span className="text-gray-800">
                      {a.filename || "Attachment"}
                    </span>
                    <ActionDropdown
                      actions={[
                        {
                          label: "Download",
                          onClick: () => handleDownloadAttachment(a),
                        },
                        {
                          label: "Delete",
                          onClick: () => handleDeleteAttachment(a.message_attachment_id),
                          className: "text-red-600",
                        },
                      ]}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {selectedTab === "GENERAL" && (
        <div className="flex items-center justify-between gap-4 mt-2">
          <button
            onClick={onClose}
            className="w-1/2 border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-4 py-2 rounded-full"
          >
          CANCEL
          </button>
          <button
            onClick={saveMessage}
            disabled={isSaving}
            className="w-1/2 bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full"
          >
          {isSaving ? 'SAVING...' : 'SAVE'}
          </button>
        </div>
        )}

        {selectedTab === "EMAIL MESSAGES" && (
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={saveMessage}
            disabled={isSaving}
            className="w-1/2 bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-3 py-1 rounded-full"
          >
          {isSaving ? 'SAVING...' : 'SAVE'}
          </button>
          <button
            onClick={previewMessage}
            className="w-1/2 border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-3 py-1 rounded-full"
          >
          PREVIEW
          </button>
          <button
            onClick={sendTestEmail}
            className="w-1/2 border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-3 py-1 rounded-full"
          >
          SEND TEST EMAIL
          </button>
          <button
            onClick={onClose}
            className="w-1/2 border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-3 py-1 rounded-full"
          >
          CANCEL
          </button>
        </div>
        )}

        {selectedTab === "ATTACHMENTS" && (
        <div className="flex items-center justify-between gap-4 mt-2">
          <button
            onClick={onClose}
            className="w-[15%] border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-3 py-1 rounded-full"
          >
          CLOSE
          </button>
        </div>
        )}
        
      </div>
    </div>
  )
};

export default EditMessageForm;

